/*
 * Copyright (c) 2023 SARL SIGMA POLYNESIA
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime, DurationUnit } from 'luxon'
import React, { useCallback, useEffect, useMemo } from 'react'
import { FormattedDate, useIntl } from 'react-intl'
import Filters from '../../../../client/js/components/Filters'
import Page from '../../../../client/js/components/Page'
import PageTitle from '../../../../client/js/components/PageTitle'
import useQueryParams from '../../../../client/js/hooks/useQueryParams'
import { findManyPayments } from '../../lib/rest/paymentAPIClient'
import CustomerInput from '../inputs/CustomerInput'
import EstateInput from '../inputs/EstateInput'
import LeaseInput from '../inputs/LeaseInput'
import Filter from '../Filter'
import PaymentListItem from './PaymentListItem'
import { useQuery } from '@tanstack/react-query'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import { parseBoolean } from '@jalik/form-parser'
import useParsedQueryParam from '../../../../client/js/hooks/useParsedQueryParam'
import { stringify } from 'qs'
import { faRefresh } from '@fortawesome/free-solid-svg-icons/faRefresh'
import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  Group,
  Radio,
  SegmentedControl,
  Stack,
  Text
} from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import { faCalendarDay } from '@fortawesome/free-solid-svg-icons/faCalendarDay'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight'
import { useNavigate } from 'react-router'
import Toolbar from '../../../../client/js/components/Toolbar'
import NoDataAlert from '../../../../client/js/components/NoDataAlert'

export type PaymentListQuery = {
  customerId?: number,
  estateId?: number,
  leaseId?: number,
  paid?: boolean,
  positive?: boolean,
  year?: number,
  month?: number,
}

const initialQuery: PaymentListQuery = {}

function PaymentListPage () {
  const intl = useIntl()

  const [, setQueryParams] = useQueryParams()
  const navigate = useNavigate()

  const customerId = useParsedQueryParam('customerId', Number)
  const estateId = useParsedQueryParam('estateId', Number)
  const expand = useParsedQueryParam('expand', parseBoolean)
  const leaseId = useParsedQueryParam('leaseId', Number)
  const month = useParsedQueryParam('month', Number)
  const paid = useParsedQueryParam('paid', parseBoolean)
  const positive = useParsedQueryParam('positive', parseBoolean)
  const year = useParsedQueryParam('year', Number)

  const unit: DurationUnit = month > 0 ? 'month' : 'year'

  const currentDt = DateTime.fromObject({ year, month })
  const prevDt = currentDt.minus({ [unit]: 1 })
  const nextDt = currentDt.plus({ [unit]: 1 })

  const query = useMemo(() => ({
    ...initialQuery,
    year,
    month,
    customerId,
    estateId,
    leaseId,
    paid,
    positive,
    expand
  }), [customerId, estateId, expand, leaseId, month, paid, positive, year])

  const paymentsQuery = useQuery({
    enabled: currentDt.isValid,
    queryKey: ['payments', query],
    queryFn: () => findManyPayments({
      ...query,
      // Convert dates to timestamps
      startDate: currentDt.startOf(unit).toISO(),
      endDate: currentDt.endOf(unit).toISO()
    })
  })
  const payments = useMemo(() => (
    (paymentsQuery.data?.data || [])
  ), [paymentsQuery.data?.data])

  const handleFilterChange = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = ev
    setQueryParams((s) => ({ ...s, [target.name]: target.value }))
  }, [setQueryParams])

  const handleFilterValueChange = useCallback((name: string) => {
    return (value: string) => {
      setQueryParams((s) => ({ ...s, [name]: value }))
    }
  }, [setQueryParams])

  const estatesFilter = useMemo(() => ({
    customerId
  }), [customerId])

  const leasesFilter = useMemo(() => ({
    customerId,
    estateId
  }), [customerId, estateId])

  useEffect(() => {
    // Display current month.
    if (year == null || year <= 0) {
      const dt = DateTime.now()
      navigate(
        { search: stringify({ year: dt.year, month: dt.month }) },
        { replace: true }
      )
    }
  }, [navigate, year])

  const title = intl.formatMessage({
    id: 'payments',
    defaultMessage: 'Paiements'
  })

  return (
    <Page>
      <Container size="xl">
        <PageTitle
          hidden
          title={title}
        />

        <Card shadow="sm">
          <Stack>
            <Toolbar>
              <Button
                disabled={currentDt.hasSame(DateTime.now(), unit)}
                component={AnchorLink}
                to={`?${stringify({
                  ...query,
                  year: DateTime.now().year,
                  month: unit === 'month' ? DateTime.now().month : undefined
                }, { skipNulls: true })}`}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faCalendarDay}
                />
              </Button>
              <Button
                component={AnchorLink}
                to={`?${stringify({
                  ...query,
                  year: prevDt.year,
                  month: unit === 'month' ? prevDt.month : undefined
                }, { skipNulls: true })}`}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faChevronLeft}
                />
              </Button>
              <Text tt="uppercase" fw="bold">
                <FormattedDate
                  value={currentDt.toJSDate()}
                  year="numeric"
                  month={unit === 'month' ? 'long' : undefined}
                />
              </Text>
              <Button
                component={AnchorLink}
                to={`?${stringify({
                  ...query,
                  year: nextDt.year,
                  month: unit === 'month' ? nextDt.month : undefined
                }, { skipNulls: true })}`}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faChevronRight}
                />
              </Button>
              <Button
                disabled={paymentsQuery.isFetching}
                loading={paymentsQuery.isFetching}
                onClick={() => {
                  paymentsQuery.refetch()
                }}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faRefresh}
                />
              </Button>

              <SegmentedControl
                data={[
                  {
                    label: intl.formatMessage({
                      id: 'monthly',
                      defaultMessage: 'Mensuel'
                    }),
                    value: 'month'
                  },
                  {
                    label: intl.formatMessage({
                      id: 'yearly',
                      defaultMessage: 'Annuel'
                    }),
                    value: 'year'
                  }
                ]}
                onChange={(v) => {
                  navigate({
                    search: stringify(v === 'month'
                      ? { ...query, year, month: 1 }
                      : { ...query, year, month: undefined })
                  })
                }}
                value={unit}
              />
            </Toolbar>

            <Divider />

            <Filters in>
              <Group>
                <Filter>
                  <Radio
                    checked={positive == null}
                    label={intl.formatMessage({
                      id: 'all',
                      defaultMessage: 'Tout'
                    })}
                    name="positive"
                    onChange={handleFilterChange}
                    value=""
                  />
                  <Radio
                    checked={positive === true}
                    label={intl.formatMessage({
                      id: 'positive_balance',
                      defaultMessage: 'Solde positif'
                    })}
                    name="positive"
                    onChange={handleFilterChange}
                    value="true"
                  />
                  <Radio
                    checked={positive === false}
                    label={intl.formatMessage({
                      id: 'negative_balance',
                      defaultMessage: 'Solde négatif'
                    })}
                    name="positive"
                    onChange={handleFilterChange}
                    value="false"
                  />
                </Filter>
                <Filter>
                  <Radio
                    checked={paid == null}
                    label={intl.formatMessage({
                      id: 'all',
                      defaultMessage: 'Tout'
                    })}
                    name="paid"
                    onChange={handleFilterChange}
                    value=""
                  />
                  <Radio
                    checked={paid === true}
                    label={intl.formatMessage({
                      id: 'paid',
                      defaultMessage: 'Payé'
                    })}
                    name="paid"
                    onChange={handleFilterChange}
                    value="true"
                  />
                  <Radio
                    checked={paid === false}
                    label={intl.formatMessage({
                      id: 'unpaid',
                      defaultMessage: 'Impayé'
                    })}
                    name="paid"
                    onChange={handleFilterChange}
                    value="false"
                  />
                </Filter>
              </Group>
              <Group>
                <Filter>
                  <CustomerInput
                    name="customerId"
                    onChange={handleFilterValueChange('customerId')}
                    value={customerId != null ? String(customerId) : null}
                  />
                </Filter>
                <Filter>
                  <EstateInput
                    queryFilter={estatesFilter}
                    name="estateId"
                    onChange={handleFilterValueChange('estateId')}
                    value={estateId != null ? String(estateId) : null}
                  />
                </Filter>
                <Filter>
                  <LeaseInput
                    queryFilter={leasesFilter}
                    name="leaseId"
                    onChange={handleFilterValueChange('leaseId')}
                    value={leaseId != null ? String(leaseId) : null}
                  />
                </Filter>
              </Group>
            </Filters>

            <Box>
              <LoadingHelper
                query={paymentsQuery}
                render={() =>
                  payments.length > 0
                    ? payments.map((payment) => (
                      <PaymentListItem
                        key={payment.customer.id}
                        payment={payment}
                      />
                    ))
                    : <NoDataAlert />}
              />
            </Box>
          </Stack>
        </Card>
      </Container>
    </Page>
  )
}

export default PaymentListPage
